import type { GenerationStatus, LearningTopicWithProgress } from '@/types'

/** Must match `MAX_PENDING_TOPICS_PER_USER` in `services/app/api/v1/endpoints/learn.py`. */
export const MAX_CONCURRENT_TOPIC_GENERATIONS = 10

/** Poll interval while topics are still being generated (SSE fallback for GCP / flaky connections). */
export const GENERATING_TOPICS_POLL_INTERVAL_MS = 5000

/** Tooltip when another job (or non-job topic) still has lessons generating. */
export const LEARN_ALL_BLOCKED_WHILE_GENERATING_TOOLTIP =
  "We're still building lessons for you. When those finish, you can add more from this job."

export function isTopicGenerating(status: GenerationStatus | undefined): boolean {
  return status === 'pending' || status === 'generating'
}

/** True when the API reports at least one module is available to open. */
export function topicHasReadyModules(
  topic: Pick<LearningTopicWithProgress, 'modules_count'>,
): boolean {
  return topic.modules_count > 0
}

/** Subtitle for pending/generating/failed topics; prefers API status_text. */
export function topicStatusText(
  topic: Pick<LearningTopicWithProgress, 'status_text' | 'generation_status'>,
): string {
  if (topic.status_text?.trim()) return topic.status_text.trim()
  if (isTopicGenerating(topic.generation_status)) return 'Generating…'
  if (topic.generation_status === 'failed') return 'Generation failed'
  return ''
}

export function topicCanRetryGeneration(
  topic: Pick<LearningTopicWithProgress, 'generation_status' | 'is_popular'>,
): boolean {
  if (topic.is_popular) return false
  return (
    topic.generation_status === 'pending' ||
    topic.generation_status === 'generating' ||
    topic.generation_status === 'failed'
  )
}

function isPendingOrGenerating(status: LearningTopicWithProgress['generation_status']): boolean {
  return isTopicGenerating(status ?? undefined)
}

export function countPendingOrGeneratingTopics(
  items: LearningTopicWithProgress[] | undefined,
): number {
  if (!items?.length) return 0
  return items.filter((t) => isPendingOrGenerating(t.generation_status)).length
}

export function hasAnyGeneratingTopics(items: LearningTopicWithProgress[] | undefined): boolean {
  return countPendingOrGeneratingTopics(items) > 0
}

export function jobHasGeneratingTopics(
  jobPostingId: number,
  items: LearningTopicWithProgress[] | undefined,
): boolean {
  if (!items?.length) return false
  return items.some(
    (t) => t.job_posting_id === jobPostingId && isPendingOrGenerating(t.generation_status),
  )
}

export function jobHasLearningTopics(
  jobPostingId: number,
  items: LearningTopicWithProgress[] | undefined,
): boolean {
  if (!items?.length) return false
  return items.some((t) => t.job_posting_id === jobPostingId)
}
